import {Material, MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {getNumberSeparators} from '@heidelbergmaterials/hproduce-number_formatting'
import {Icon, Tooltip, icon as fundamentalIcon} from '@hm/fundamental'
import {Autocomplete, Box, TextField} from '@mui/material'
import {DatePicker} from '@mui/x-date-pickers'
import moment from 'moment'
import {Control, Controller, FieldValues, Path, UseFormClearErrors} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import NumericFormat from 'react-number-format'

import {ControlledCheckbox} from '../../../common/components'
import {ControlledTimePickerField} from '../../../common/components/form/ControlledTimePickerField'
import {UnitType} from '../../../common/enums'
import {Config, FieldType} from '../../types'

import {ConfigTextField} from './ConfigTextField'
import {MaterialSelection} from './MaterialSelection'

import {getIsValueFieldValid} from '@settings/modules/common/helpers'

interface ConfigControlledFieldProps<T extends FieldValues> {
  control: Control<T>
  materials: Material[]
  field: FieldType
  upmId?: string
  translateOptions?: boolean
  disabled?: boolean
  endDate?: string | undefined
  startDate?: string | undefined
  clearErrors: UseFormClearErrors<Config>
  labelParams?: {[key: string]: number | string}
  materialType?: MaterialType
  ignoreValidation?: boolean
  isOptional?: boolean
}

export const ConfigControlledField = <T extends FieldValues>({
  control,
  materials,
  field,
  upmId,
  startDate,
  endDate,
  disabled,
  clearErrors,
  ignoreValidation,
  isOptional
}: ConfigControlledFieldProps<T>): JSX.Element => {
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const {
    label,
    name,
    testId,
    type,
    options,
    translateOptions,
    labelParams,
    materialType,
    showAllMaterials,
    minExclusiveValue,
    maxLength,
    classificationCode,
    classificationCodeValue,
    classificationMaterialType,
    showTooltip,
    tooltipKey
  } = field

  const selectOptions = options && options?.length > 0 ? options : materials

  const {decimalSeparator, thousandsSeparator} = getNumberSeparators(language)

  const renderField = (): JSX.Element => {
    switch (type) {
      case 'Select':
        return (
          <MaterialSelection
            name={name as Path<T>}
            control={control}
            materials={selectOptions as Material[]}
            label={t(`plantSetup.${label}`)}
            testId={`${testId}-${upmId}`}
            key={`${label}-key`}
            optional={isOptional}
            materialType={materialType}
            showAllMaterials={showAllMaterials}
            translateOptions={translateOptions}
            classificationCode={classificationCode}
            classificationCodeValue={classificationCodeValue}
            classificationMaterialType={classificationMaterialType}
          />
        )
      case 'Numeric':
        return (
          <Controller
            name={name as Path<T>}
            control={control}
            rules={{
              required: !isOptional && t('error.required'),
              validate: (value) => {
                return ignoreValidation
                  ? true
                  : getIsValueFieldValid(value, UnitType.Numeric, t, {
                      minExclusiveValue: minExclusiveValue
                    })
              }
            }}
            render={({field: {value, ref, onChange}, fieldState: {error}}) => {
              const helperText = (error && error.message) || ''

              return (
                <NumericFormat
                  {...dataTestId(`${testId}-${upmId}`)}
                  type="text"
                  defaultValue={''}
                  fullWidth
                  name={name}
                  isNumericString
                  error={!!error}
                  helperText={helperText}
                  inputRef={ref}
                  allowEmptyFormatting
                  allowLeadingZeros={false}
                  allowNegative={true}
                  onValueChange={(values) => onChange(values.value)}
                  value={value}
                  decimalSeparator={decimalSeparator}
                  thousandSeparator={thousandsSeparator}
                  allowedDecimalSeparators={['.', ',']}
                  customInput={TextField}
                  label={t(`plantSetup.${label}`)}
                  variant="outlined"
                  disabled={disabled}
                />
              )
            }}
          />
        )
      case 'Text':
        return (
          <ConfigTextField
            control={control}
            name={name as Path<T>}
            label={label}
            testId={testId}
            upmId={upmId}
            disabled={disabled}
            isOptional={isOptional}
            maxLength={maxLength}
          />
        )
      case 'Autocomplete':
        return (
          <Controller
            name={name as Path<T>}
            control={control}
            rules={{
              required: !isOptional && t('error.required')
            }}
            render={({field: {ref, value, onChange}, fieldState: {error}}) => {
              const helperText = (error && error.message) || ''

              return (
                <Autocomplete
                  {...dataTestId(`${field.testId}-${upmId}`)}
                  options={(selectOptions || []) as string[]}
                  fullWidth
                  disableClearable
                  getOptionLabel={(option) =>
                    translateOptions && option ? t(`plantSetup.configOptions.${option}`) : option
                  }
                  disablePortal
                  disabled={disabled}
                  onChange={(_, selectedOption) => onChange(selectedOption)}
                  value={value}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        label={t(`plantSetup.${label}`, labelParams ?? {})}
                        variant="outlined"
                        error={!!error}
                        helperText={helperText}
                        inputRef={ref}
                      />
                    )
                  }}
                />
              )
            }}
          />
        )
      case 'Date':
        return (
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            <Controller
              name={'commissStartDate' as Path<T>}
              control={control}
              rules={{
                required: !isOptional && t('error.required'),
                validate: (value) => {
                  if (!value || !endDate || ignoreValidation) return true
                  return moment(value).isSameOrBefore(endDate) || t('error.startAfterEndDate')
                }
              }}
              render={({field, fieldState}) => (
                <DatePicker
                  {...field}
                  inputFormat="DD.MM.YYYY"
                  label={t('plantSetup.commissStartDate')}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      {...dataTestId(`start-date-${upmId}`)}
                    />
                  )}
                  onChange={(value) => {
                    field.onChange(value)
                    clearErrors('commissStartDate')
                    clearErrors('commissEndDate')
                  }}
                />
              )}
            />
            <span>-</span>
            <Controller
              name={'commissEndDate' as Path<T>}
              control={control}
              rules={{
                required: !isOptional && t('error.required'),
                validate: (value) => {
                  if (!value || !startDate || ignoreValidation) return true
                  return moment(value).isSameOrAfter(startDate) || t('error.endBeforeStartDate')
                }
              }}
              render={({field, fieldState}) => (
                <DatePicker
                  {...field}
                  inputFormat="DD.MM.YYYY"
                  disabled={disabled}
                  label={t('plantSetup.commissEndDate')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...dataTestId(`end-date-${upmId}`)}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                  onChange={(value) => {
                    field.onChange(value)
                    clearErrors('commissStartDate')
                    clearErrors('commissEndDate')
                  }}
                />
              )}
            />
          </Box>
        )

      case 'Checkbox':
        return (
          <ControlledCheckbox
            {...dataTestId(`${name}-${upmId}`)}
            formDataName={name as Path<T>}
            parseValue={(value) => value === 'true'}
            formatValue={(value) => String(value)}
            label={t(`plantSetup.${label}`)}
            control={control}
            disabled={disabled}
          />
        )
      case 'TimePicker':
        return (
          <ControlledTimePickerField
            name={name as Path<T>}
            control={control}
            label={t(`plantSetup.${label}`)}
            testId={`${testId}-${upmId}`}
            disabled={disabled}
          />
        )
      default:
        return <div />
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">{renderField()}</div>
      <div className="w-5 flex-shrink-0 flex items-center justify-center">
        {showTooltip && tooltipKey && (
          <Tooltip content={t(`plantSetup.fieldsTooltips.${tooltipKey}`)} delayMs={0}>
            <div data-cy={`field-tooltip-${tooltipKey}`}>
              <Icon
                path={fundamentalIcon.mdiInformationOutline}
                size="regular"
                className="text-neutral-500"
              />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
